import React, { useEffect, useMemo, useState } from 'react';
import { Button } from '../../../../../../base/components/Button';
import { Icon } from '../../../../../../base/components/Icon';
import { useAppDispatch, useAppSelector } from '../../../../../../base/hooks';
import { DatePeriod } from '../../../enums';
import { responsesActions } from '../../../redux/slices/responsesSlice';
import { mapDatePeriodToText } from '../../../utils';

import moment from 'moment';
import { __t } from '../../../../../../base/utils/t';
import './styles.scss';
import { DatePickerModal } from '../DatePickerModal';

interface Props {
    className?: string;
}

export const DateRangeSelectMobile = ({ className = '' }: Props) => {
    const dispatch = useAppDispatch();
    const { datePeriod, dateFrom, dateTo } = useAppSelector((state) => state.employerResponses.filters);

    const [showSelectPopup, setShowSelectPopup] = useState(false);
    const [datePeriodLocalValue, setDatePeriodLocalValue] = useState<DatePeriod>(datePeriod);
    const [dateFromLocalValue, setDateFromLocalValue] = useState<Date | undefined>(dateFrom);
    const [dateToLocalValue, setDateToLocalValue] = useState<Date | undefined>(dateTo);

    const [showDatePickerModal, setShowDatePickerModal] = useState(false);

    useEffect(() => {
        setDatePeriodLocalValue(datePeriod);
        setDateFromLocalValue(dateFrom);
        setDateToLocalValue(dateTo);
    }, [datePeriod, dateFrom, dateTo]);

    useEffect(() => {
        if (showSelectPopup) {
            document.documentElement.setAttribute('DisableScroll', 'true');
            return;
        }
        document.documentElement.removeAttribute('DisableScroll');

        return () => {
            document.documentElement.removeAttribute('DisableScroll');
        };
    }, [showSelectPopup]);

    const clearFilter = (event: React.MouseEvent<HTMLDivElement>) => {
        event.stopPropagation();
        dispatch(responsesActions.setFiltersDatePeriod(DatePeriod.AllTime));
        dispatch(responsesActions.setFiltersDates({ dateFrom: undefined, dateTo: undefined }));
    };

    const onChangeHandler = (value: DatePeriod) => {
        setDatePeriodLocalValue(value);

        if (value === DatePeriod.CustomPeriod) {
            dispatch(responsesActions.setFiltersDatePeriod(value));
            togglePopupVisibility();
            setShowDatePickerModal(true);
            return;
        }

        let dateFromNewValue: Date | undefined = undefined;
        let dateToNewValue: Date | undefined = undefined;

        switch (value) {
            case DatePeriod.Today:
                dateFromNewValue = moment().startOf('day').toDate();
                break;
            case DatePeriod.Yesterday:
                dateFromNewValue = moment().subtract(1, 'days').startOf('day').toDate();
                dateToNewValue = moment().subtract(1, 'days').endOf('day').toDate();
                break;
            case DatePeriod.Last7Days:
                dateFromNewValue = moment().subtract(7, 'days').startOf('day').toDate();
                break;
            case DatePeriod.Last30Days:
                dateFromNewValue = moment().subtract(30, 'days').startOf('day').toDate();
                break;
            case DatePeriod.AllTime:
            default:
                break;
        }
        setDateFromLocalValue(dateFromNewValue);
        setDateToLocalValue(dateToNewValue);
    };

    const submitFilterButton = () => {
        dispatch(responsesActions.setFiltersDatePeriod(datePeriodLocalValue));
        dispatch(responsesActions.setFiltersDates({ dateFrom: dateFromLocalValue, dateTo: dateToLocalValue }));
        togglePopupVisibility();
    };

    const togglePopupVisibility = () => {
        setShowSelectPopup(!showSelectPopup);
    };

    const closePopupHandler = () => {
        setDatePeriodLocalValue(datePeriod);
        setDateFromLocalValue(dateFrom);
        setDateToLocalValue(dateTo);
        togglePopupVisibility();
    };

    const hideDatePickerModal = () => setShowDatePickerModal(false);

    const datePickerModalCloseCallback = () => {
        dispatch(responsesActions.setFiltersDatePeriod(DatePeriod.AllTime));
        dispatch(responsesActions.setFiltersDates({ dateFrom: undefined, dateTo: undefined }));
    };

    const renderValue = useMemo(() => {
        if (datePeriod !== DatePeriod.CustomPeriod) {
            return mapDatePeriodToText(datePeriod);
        }

        return dateFrom && dateTo
            ? `${moment(dateFrom).format('DD MMM YYYY')}-${moment(dateTo).format('DD MMM YYYY')}`
            : '';
    }, [datePeriod, dateFrom, dateTo]);

    return (
        <>
            <div className={`responses-filters-mobile-select ${className}`} onClick={togglePopupVisibility}>
                {datePeriod !== DatePeriod.AllTime && (
                    <div onClick={clearFilter} className='responses-filters-mobile-select__clear-filter-button'>
                        <Icon icon='xmark-regular' size='14px' className='responses-filters-mobile-select__icon' />
                    </div>
                )}
                <span className='responses-filters-mobile-select__display-value-text'>{renderValue}</span>
                <Icon icon='chevron-down-regular' size='14px' className='responses-filters-mobile-select__icon' />
            </div>

            {showSelectPopup && (
                <>
                    <div
                        className='responses-filters-mobile-select-popup__background'
                        onClick={closePopupHandler}
                    ></div>
                    <div className='responses-filters-mobile-select-popup'>
                        <div
                            onClick={closePopupHandler}
                            className='responses-filters-mobile-select-popup__close-button'
                        >
                            <Icon icon='xmark-regular' size='24px' color='white' />
                        </div>
                        <div className='responses-filters-mobile-select-popup__content'>
                            <div
                                className='responses-filters-mobile-select-popup__item'
                                onClick={() => onChangeHandler(DatePeriod.Today)}
                            >
                                <div
                                    className={`responses-filters-mobile-select-popup__check-icon ${
                                        datePeriodLocalValue === DatePeriod.Today
                                            ? 'responses-filters-mobile-select-popup__check-icon--checked'
                                            : ''
                                    }`}
                                >
                                    {datePeriodLocalValue === DatePeriod.Today ? (
                                        <Icon icon='check' color='white' size='12px' />
                                    ) : null}
                                </div>
                                <span className='responses-filters-mobile-select-popup__item-text'>
                                    {mapDatePeriodToText(DatePeriod.Today)}
                                </span>
                            </div>

                            <div
                                className='responses-filters-mobile-select-popup__item'
                                onClick={() => onChangeHandler(DatePeriod.Yesterday)}
                            >
                                <div
                                    className={`responses-filters-mobile-select-popup__check-icon ${
                                        datePeriodLocalValue === DatePeriod.Yesterday
                                            ? 'responses-filters-mobile-select-popup__check-icon--checked'
                                            : ''
                                    }`}
                                >
                                    {datePeriodLocalValue === DatePeriod.Yesterday ? (
                                        <Icon icon='check' color='white' size='12px' />
                                    ) : null}
                                </div>
                                <span className='responses-filters-mobile-select-popup__item-text'>
                                    {mapDatePeriodToText(DatePeriod.Yesterday)}
                                </span>
                            </div>

                            <div
                                className='responses-filters-mobile-select-popup__item'
                                onClick={() => onChangeHandler(DatePeriod.Last7Days)}
                            >
                                <div
                                    className={`responses-filters-mobile-select-popup__check-icon ${
                                        datePeriodLocalValue === DatePeriod.Last7Days
                                            ? 'responses-filters-mobile-select-popup__check-icon--checked'
                                            : ''
                                    }`}
                                >
                                    {datePeriodLocalValue === DatePeriod.Last7Days ? (
                                        <Icon icon='check' color='white' size='12px' />
                                    ) : null}
                                </div>
                                <span className='responses-filters-mobile-select-popup__item-text'>
                                    {mapDatePeriodToText(DatePeriod.Last7Days)}
                                </span>
                            </div>

                            <div
                                className='responses-filters-mobile-select-popup__item'
                                onClick={() => onChangeHandler(DatePeriod.Last30Days)}
                            >
                                <div
                                    className={`responses-filters-mobile-select-popup__check-icon ${
                                        datePeriodLocalValue === DatePeriod.Last30Days
                                            ? 'responses-filters-mobile-select-popup__check-icon--checked'
                                            : ''
                                    }`}
                                >
                                    {datePeriodLocalValue === DatePeriod.Last30Days ? (
                                        <Icon icon='check' color='white' size='12px' />
                                    ) : null}
                                </div>
                                <span className='responses-filters-mobile-select-popup__item-text'>
                                    {mapDatePeriodToText(DatePeriod.Last30Days)}
                                </span>
                            </div>

                            <div
                                className='responses-filters-mobile-select-popup__item'
                                onClick={() => onChangeHandler(DatePeriod.CustomPeriod)}
                            >
                                <div
                                    className={`responses-filters-mobile-select-popup__check-icon ${
                                        datePeriodLocalValue === DatePeriod.CustomPeriod
                                            ? 'responses-filters-mobile-select-popup__check-icon--checked'
                                            : ''
                                    }`}
                                >
                                    {datePeriodLocalValue === DatePeriod.CustomPeriod ? (
                                        <Icon icon='check' color='white' size='12px' />
                                    ) : null}
                                </div>
                                <span className='responses-filters-mobile-select-popup__item-text'>
                                    {mapDatePeriodToText(DatePeriod.CustomPeriod)}
                                </span>
                            </div>

                            <Button
                                onClick={submitFilterButton}
                                className='responses-filters-mobile-select-popup__submit-button'
                            >
                                {__t('Фільтрувати відгуки')}
                            </Button>
                        </div>
                    </div>
                </>
            )}
            {datePeriodLocalValue === DatePeriod.CustomPeriod && (
                <DatePickerModal
                    visible={showDatePickerModal}
                    closeHandler={hideDatePickerModal}
                    onModalCloseCallback={datePickerModalCloseCallback}
                />
            )}
        </>
    );
};
